import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Save, Replay } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableContainer: {
    overflowX: "auto",
  },
  stickyCol: {
    position: "sticky",
    left: 0,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.paper
        : "#fff",
    zIndex: 1,
    minWidth: 160,
    fontWeight: 600,
  },
  queueHeader: {
    minWidth: 120,
    textAlign: "center",
    fontWeight: 600,
  },
  colorDot: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  centeredCell: {
    textAlign: "center",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
  },
  toolbarButtons: {
    display: "flex",
    gap: theme.spacing(1),
  },
}));

// Deep-clone the association map
const cloneMap = (map) => {
  const clone = {};
  Object.keys(map).forEach((qid) => {
    clone[qid] = new Set(map[qid]);
  });
  return clone;
};

// Build association map: { [queueId]: Set<userId> }
const buildAssociationMap = (queues) => {
  const map = {};
  queues.forEach((q) => {
    map[q.id] = new Set((q.users || []).map((u) => u.id));
  });
  return map;
};

const QueueUserAssociation = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  // Server-truth state
  const [savedState, setSavedState] = useState({});
  // In-progress edits
  const [pendingState, setPendingState] = useState({});

  const fetchAllUsers = async () => {
    let page = 1;
    let allUsers = [];
    let hasMore = true;
    while (hasMore) {
      const res = await api.get("/users", { params: { pageNumber: page } });
      const { users = [], hasMore: more = false } = res.data;
      allUsers = allUsers.concat(users);
      hasMore = more;
      page += 1;
    }
    return allUsers;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [queuesRes, usersData] = await Promise.all([
        api.get("/queue"),
        fetchAllUsers(),
      ]);

      const queuesData = queuesRes.data;

      const assocMap = buildAssociationMap(queuesData);

      setQueues(queuesData);
      setUsers(usersData);      setSavedState(assocMap);
      setPendingState(cloneMap(assocMap));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = (queueId, userId) => {
    setPendingState((prev) => {
      const next = cloneMap(prev);
      if (next[queueId].has(userId)) {
        next[queueId].delete(userId);
      } else {
        next[queueId].add(userId);
      }
      return next;
    });
  };

  const handleReset = () => {
    setPendingState(cloneMap(savedState));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Find queues whose user sets changed
      const changedQueues = queues.filter((q) => {
        const saved = savedState[q.id] || new Set();
        const pending = pendingState[q.id] || new Set();
        if (saved.size !== pending.size) return true;
        for (const uid of pending) {
          if (!saved.has(uid)) return true;
        }
        return false;
      });

      await Promise.all(
        changedQueues.map((q) =>
          api.put(`/queue/${q.id}`, {
            name: q.name,
            color: q.color,
            userIds: Array.from(pendingState[q.id] || []),
          })
        )
      );

      setSavedState(cloneMap(pendingState));
      toast.success(i18n.t("queueUserAssociation.saveSuccess"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("queueUserAssociation.title")}</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.toolbarButtons}>
            <Button
              variant="outlined"
              color="default"
              startIcon={<Replay />}
              onClick={handleReset}
              disabled={saving || loading}
            >
              {i18n.t("queueUserAssociation.reset")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {i18n.t("queueUserAssociation.save")}
            </Button>
          </div>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.stickyCol}>
                    {i18n.t("queueUserAssociation.userColumn")}
                  </TableCell>
                  {queues.map((queue) => (
                    <TableCell key={queue.id} className={classes.queueHeader}>
                      <span
                        className={classes.colorDot}
                        style={{ backgroundColor: queue.color || "#ccc" }}
                      />
                      {queue.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={queues.length + 1} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("queueUserAssociation.noUsers")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {users.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell className={classes.stickyCol}>
                      {u.name}
                    </TableCell>
                    {queues.map((queue) => (
                      <TableCell key={queue.id} className={classes.centeredCell}>
                        <Checkbox
                          size="small"
                          checked={!!(pendingState[queue.id] && pendingState[queue.id].has(u.id))}
                          onChange={() => handleToggle(queue.id, u.id)}
                          disabled={saving}
                          color="primary"
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </MainContainer>
  );
};

export default QueueUserAssociation;
